import { brand, colors, palette } from 'theme/tokens';

/**
 * PARTNER-ADMIN DYNAMIC BRANDING — the one place a saved brand becomes pixels.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * WHAT THIS SOLVES
 * A partner (or an admin acting for them) configures Primary / Secondary /
 * Accent plus four logo assets on Settings -> Branding. Before this module the
 * portal's own chrome ignored every one of them:
 *
 *   - `useThemeStore` built the antd theme from the STATIC `partnerThemes` map,
 *     so the saved colours reached the "Live Preview" card and nothing else.
 *   - ~81 literal `#7C5CFC` sat in inline styles across 13 files.
 *   - The sidebar mark was a hardcoded purple "Z" square and the favicon was
 *     whatever `public/favicon.ico` shipped.
 *
 * So a partner saved their colours and the console stayed Zenvoya purple.
 *
 * HOW IT IS FIXED — TWO CHANNELS, ONE SOURCE
 * Both are fed from the same resolved branding object, so they cannot drift:
 *
 *   1. `buildThemeConfig(branding)` -> the antd `ConfigProvider` theme. This
 *      repaints every antd component (buttons, tabs, switches, menu, focus
 *      rings) because antd derives its whole palette from `token.colorPrimary`.
 *      antd's algorithm needs REAL hex values here — a `var()` cannot be fed to
 *      it, which is why that builder returns literals and not custom properties.
 *
 *   2. `applyBrandTokens(branding)` -> `--pa-brand-*` CSS custom properties on
 *      <html>, for everything antd does not own: our own inline styles, the
 *      polish stylesheet, the sidebar. Those call sites now read
 *      `var(--pa-brand-primary)` and follow the brand for free.
 *
 * WHY THE VARIABLES GO ON <html> AND NOT ON `#partner-admin-root`
 * antd renders Modals, Selects, Dropdowns and Tooltips through a PORTAL into
 * `document.body` — outside `#partner-admin-root`. A variable defined on that
 * root would be out of scope inside those layers and every `var()` in them
 * would silently fall back. The names are namespaced `--pa-*` precisely so that
 * defining them globally cannot collide with the /admin or /legacyAdmin
 * sections that share this same document.
 *
 * EVERY `var()` CALL SITE MUST CARRY THE OLD LITERAL AS ITS FALLBACK
 * (`var(--pa-brand-primary, #7C5CFC)`). A screen rendered before the store's
 * first write — or in a future context that never mounts the partner portal —
 * then paints exactly what it painted before this change.
 */

/**
 * The safety net: what a partner who has saved no branding gets — the active
 * brand's colours (`colors.partnerDefaultBranding`, from theme/brands). For
 * Zenvoya that is its house palette, the same three colours the Branding
 * page's DEFAULT_BRAND_COLORS and the traveller app default to. It used to be the
 * pre-dynamic-branding demo look (#7C5CFC / #c9a84c / #1E1B3A), which showed a
 * brand-new partner a colour Zenvoya does not use (SMB-4226).
 */
export const FALLBACK_BRANDING = {
  name: 'Zenvoya',
  ...colors.partnerDefaultBranding,
  fontFamily: 'Inter',
  logoUrl: '',
  logoDarkUrl: '',
  iconUrl: '',
  faviconUrl: '',
};

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

export const isHexColor = (value) => HEX.test(String(value ?? '').trim());

/** "#abc" | "abc" | "#aabbcc" -> [r,g,b]; null when it is not a hex colour. */
export const parseHex = (value) => {
  const raw = String(value ?? '').trim();
  if (!HEX.test(raw)) return null;
  let hex = raw.replace('#', '');
  if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
  return [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16),
  ];
};

const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));

const toHex = (rgb) => `#${rgb.map((n) => clamp(n).toString(16).padStart(2, '0')).join('').toUpperCase()}`;

/** "124, 92, 252" — the triple form, so a colour can be given an alpha. */
export const rgbTriple = (color) => {
  const rgb = parseHex(color);
  return rgb ? rgb.join(', ') : '';
};

/**
 * Mix `color` toward white (`amount` > 0) or black (`amount` < 0).
 *
 * The branding page collects ONE primary, but the console needs a ramp — a tint
 * for selected rows, a deep step for hover. Those are derived rather than asked
 * for, because a partner has no ramp to give.
 */
export const mix = (color, amount) => {
  const rgb = parseHex(color);
  if (!rgb) return color;
  const target = amount >= 0 ? 255 : 0;
  const t = Math.abs(amount);
  return toHex(rgb.map((c) => c + (target - c) * t));
};

/**
 * Black or white — whichever is actually readable on `background`.
 *
 * ── WHY THIS IS NEEDED ──────────────────────────────────────────────────────
 * Anywhere a brand colour is used as a FILL with text or an icon on top, the
 * foreground was hardcoded `#fff`. That is safe for a primary — the branding
 * page pushes partners to keep it AA against white, and #6C00F6 is 7.07:1 — but
 * NOT for the secondary or accent, which the page explicitly allows to fail
 * contrast and even ships failing in its own sample data.
 *
 * The visible symptom: the preview's user avatar is filled with the secondary
 * and captioned with a white initial. On #A1F919 that is 1.31:1 — the letter is
 * effectively invisible. Black on the same green is 16.08:1.
 *
 * ── THE MATH ────────────────────────────────────────────────────────────────
 * WCAG relative luminance, then the standard contrast ratio against white.
 * WHITE IS PREFERRED: it is kept whenever it reaches 3:1 (WCAG's floor for
 * bold/large text and UI components), and black is used only below that.
 *
 * It used to pick whichever of black or white scored higher. On a mid-tone
 * orange like #F55905 that chose black (6.3:1 vs white's 3.3:1), so the header
 * badge showed black digits while the "Save Changes" button on the same colour
 * had white text — two answers for one brand colour. Buttons are always white,
 * so preferring white keeps the chrome consistent, and the 3:1 floor still
 * flips to black where white genuinely disappears (#A1F919 is 1.31:1).
 *
 * Cost is a handful of multiplications on a 3-byte colour, called only where a
 * brand fill is painted — a few nodes per render, no allocation, no layout.
 * Falls back to white for a non-hex input, which is the behaviour it replaces.
 */
const relativeLuminance = (rgb) => {
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const contrastRatio = (colorA, colorB) => {
  const a = parseHex(colorA);
  const b = parseHex(colorB);
  if (!a || !b) return 1;
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
};

const WHITE_TEXT_MIN_CONTRAST = 3;

export const readableTextOn = (background) => {
  if (!parseHex(background)) return palette.white;
  return contrastRatio(background, palette.white) >= WHITE_TEXT_MIN_CONTRAST
    ? palette.white
    : palette.black;
};

/** Linear blend of two hexes: t=0 -> `from`, t=1 -> `to`. */
export const blend = (from, to, t) => {
  const a = parseHex(from);
  const b = parseHex(to);
  if (!a || !b) return from;
  return toHex(a.map((c, i) => c + (b[i] - c) * t));
};

/**
 * The dark chrome behind the sidebar — KEPT BUT UNUSED. The sidebar was derived from the primary until product
 * decided the navigation stays the same on every tenant. Left here — with its
 * reasoning intact — because it is the non-obvious half of the problem: if a
 * dark branded surface is ever wanted again, blending 18% of the brand into a
 * near-black is what keeps it legible for white text at ANY primary the picker
 * can produce, where simply darkening the primary is not.
 */
// eslint-disable-next-line no-unused-vars
export const siderBgFor = (primary) => blend(primary, '#0E0C1A', 0.82);
// eslint-disable-next-line no-unused-vars
export const siderSubBgFor = (primary) => blend(primary, '#08070F', 0.88);

/** The five-step primary ramp the console paints with. */
export const buildBrandRamp = (primary) => ({
  primary,
  light: mix(primary, 0.9),
  light2: mix(primary, 0.6),
  dark: mix(primary, -0.28),
  dark2: mix(primary, -0.55),
});

/**
 * Fill in anything missing or malformed.
 *
 * The branding page happily saves a colour that FAILS contrast (its own sample
 * data carries a 1.3:1 Secondary marked "Fail"), and the §30.1 response returns
 * nulls before a partner's first save. So nothing downstream may assume the
 * fields are present or sane — every colour is format-checked here and falls
 * back rather than reaching the theme as `undefined`.
 */
export const withBrandingFallbacks = (branding) => {
  const b = branding ?? {};
  // The store and the wire disagree on the accent's name: the branding page
  // keeps it as `headerBg` while the server persists it as `accentColor` /
  // `headerBackgroundColor`. Accept all three so either caller works.
  const accent = b.accentColor ?? b.headerBg ?? b.headerBackgroundColor;
  return {
    name: b.name || FALLBACK_BRANDING.name,
    primaryColor: isHexColor(b.primaryColor) ? b.primaryColor : FALLBACK_BRANDING.primaryColor,
    secondaryColor: isHexColor(b.secondaryColor) ? b.secondaryColor : FALLBACK_BRANDING.secondaryColor,
    accentColor: isHexColor(accent) ? accent : FALLBACK_BRANDING.accentColor,
    fontFamily: b.fontFamily || FALLBACK_BRANDING.fontFamily,
    logoUrl: b.logoUrl || '',
    logoDarkUrl: b.logoDarkUrl || '',
    iconUrl: b.iconUrl || '',
    faviconUrl: b.faviconUrl || '',
  };
};

/**
 * The favicon that shipped, remembered on the first write.
 *
 * Clearing the favicon slot must restore the app's own icon rather than leave
 * the previous partner's sitting in the tab, and the original href is only
 * knowable before the first overwrite.
 *
 * The app's own icon is the active brand's (theme/brands) when it has one:
 * the boot write below runs at import, BEFORE `applyBrandFavicon` in index.js
 * swaps the tab icon, so the remembered href is always index.html's Zenvoya
 * one and would put it back on every partner sign-in.
 */
let shippedFaviconHref = null;

/**
 * Repoint the browser-tab icon.
 *
 * Deliberately defensive: a favicon is cosmetic and must never be able to throw
 * during theme application.
 */
export const applyFavicon = (faviconUrl) => {
  try {
    const link = document.querySelector('link[rel="icon"]');
    if (!link) return;
    if (shippedFaviconHref === null) shippedFaviconHref = link.getAttribute('href') || '';
    const next = faviconUrl || brand.favicon || shippedFaviconHref;
    if (next && link.getAttribute('href') !== next) link.setAttribute('href', next);
  } catch {
    /* never let the tab icon break app init */
  }
};

/**
 * Push a resolved branding onto the document: CSS custom properties + favicon.
 *
 * Idempotent — safe to call on every colour-picker keystroke, which is exactly
 * what the branding page does.
 */
export const applyBrandTokens = (branding) => {
  if (typeof document === 'undefined') return;
  const b = withBrandingFallbacks(branding);
  const ramp = buildBrandRamp(b.primaryColor);
  const root = document.documentElement;

  const set = (name, value) => root.style.setProperty(name, value);

  set('--pa-brand-primary', ramp.primary);
  set('--pa-brand-primary-light', ramp.light);
  set('--pa-brand-primary-light-2', ramp.light2);
  set('--pa-brand-primary-dark', ramp.dark);
  set('--pa-brand-primary-dark-2', ramp.dark2);
  set('--pa-brand-secondary', b.secondaryColor);
  set('--pa-brand-accent', b.accentColor);
  // NO `--pa-sider-*`: the left sidebar deliberately does not follow the brand
  // (see the note in AppLayout.jsx), so publishing a derived panel colour would
  // only invite a future call site to use it.

  // RGB TRIPLES, for `rgba(var(--pa-brand-primary-rgb), 0.15)` tints.
  //
  // A variable holding "#6C00F6" cannot be given an alpha — `rgba()` needs three
  // numbers, not a hex. The triple form solves that with nothing but custom
  // properties, so it works everywhere they do. `color-mix()` would be the
  // modern alternative but is dropped wholesale by anything older than 2023,
  // which for a selection highlight means it disappears rather than degrades.
  set('--pa-brand-primary-rgb', rgbTriple(ramp.primary));
  set('--pa-brand-secondary-rgb', rgbTriple(b.secondaryColor));
  set('--pa-brand-accent-rgb', rgbTriple(b.accentColor));

  applyFavicon(b.faviconUrl);
};
